import { Platform } from 'react-native';

import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';
import type { SubscriptionPlan } from '../types/premiumFeatures';

import { enhancedStripeService } from './enhancedStripeService';
import type { UserSubscription } from './entitlementService';
import {
  reactNativePaymentService as mobileReactNativePaymentService,
  type PurchaseResult as StorePurchaseResult,
  type StoreEventPassSku,
  type StoreSubscriptionSku,
} from './reactNativePaymentService';
import reactNativePaymentServiceWeb from './reactNativePaymentService.web';
import { stripeService } from './stripeService';
import { subscriptionService } from './subscriptionService';

type BillingInterval = 'monthly' | 'annual';
type PaymentProviderId = 'stripe' | 'apple' | 'google';
type PaymentMetadata = Record<string, unknown>;
type WebhookData = Record<string, unknown>;
type CanonicalPaidPlanId = 'premium';
type LegacyPlanId =
  | 'individual'
  | 'family'
  | 'group_small'
  | 'group_medium'
  | 'group_large'
  | 'church_small'
  | 'church_medium'
  | 'church_large';
type ReactNativePaymentAdapter = {
  purchaseSubscription(sku: StoreSubscriptionSku): Promise<StorePurchaseResult>;
  purchaseEventPass(sku: StoreEventPassSku): Promise<StorePurchaseResult>;
};
type StripeSubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete' | 'unpaid';

const LEGACY_PLAN_ID_MAP: Record<LegacyPlanId, CanonicalPaidPlanId> = {
  individual: 'premium',
  family: 'premium',
  group_small: 'premium',
  group_medium: 'premium',
  group_large: 'premium',
  church_small: 'premium',
  church_medium: 'premium',
  church_large: 'premium',
};

const SUPPORTED_PAID_PLAN_IDS = new Set<CanonicalPaidPlanId>(['premium']);

const MOBILE_PLAN_SKU_MAP: Partial<Record<CanonicalPaidPlanId, Record<BillingInterval, StoreSubscriptionSku>>> = {
  premium: {
    monthly: 'kbq_family_monthly',
    annual: 'kbq_family_annual',
  },
};

const EVENT_PASS_SKU_MAP: Record<string, StoreEventPassSku> = {
  event_basic: 'kbq_event_basic',
  event_standard: 'kbq_event_standard',
  event_premium: 'kbq_event_premium',
  event_mega: 'kbq_event_mega',
};

const reactNativePaymentService: ReactNativePaymentAdapter =
  typeof window !== 'undefined'
    ? reactNativePaymentServiceWeb
    : mobileReactNativePaymentService;

const isStripeSubscriptionStatus = (value: unknown): value is StripeSubscriptionStatus => {
  return (
    value === 'active' ||
    value === 'canceled' ||
    value === 'past_due' ||
    value === 'trialing' ||
    value === 'incomplete' ||
    value === 'unpaid'
  );
};

const isLegacyPlanId = (planId: string): planId is LegacyPlanId =>
  Object.prototype.hasOwnProperty.call(LEGACY_PLAN_ID_MAP, planId);

const isCanonicalPaidPlanId = (planId: string): planId is CanonicalPaidPlanId =>
  SUPPORTED_PAID_PLAN_IDS.has(planId as CanonicalPaidPlanId);

const normalizePlanId = (planId: string): CanonicalPaidPlanId => {
  if (isLegacyPlanId(planId)) {
    return LEGACY_PLAN_ID_MAP[planId];
  }

  if (isCanonicalPaidPlanId(planId)) {
    return planId;
  }

  throw new Error(`Unsupported subscription plan: ${planId}`);
};

export interface PaymentProvider {
  id: 'stripe' | 'apple' | 'google';
  name: string;
  enabled: boolean;
  supportedMethods: ('subscription' | 'one_time')[];
}

export interface CreateSubscriptionRequest {
  userId: string;
  planId: string;
  billingInterval: BillingInterval;
  provider: PaymentProviderId;
  customerEmail?: string;
  paymentMethodId?: string;
}

export interface CreatePaymentRequest {
  userId: string;
  itemId: string;
  amount: number;
  currency: string;
  provider: PaymentProviderId;
  metadata?: PaymentMetadata;
}

export interface PaymentResult {
  success: boolean;
  paymentId?: string;
  subscriptionId?: string;
  clientSecret?: string; // For client-side payment completion
  checkoutUrl?: string;
  error?: string;
  requiresAction?: boolean;
  nextAction?: Record<string, unknown>;
}

export interface ChangeSubscriptionRequest {
  userId: string;
  newPlanId: string;
  newBillingInterval?: BillingInterval;
  prorationBehavior?: 'immediate' | 'next_period';
}

export interface SubscriptionChangeResult {
  success: boolean;
  subscriptionId?: string;
  prorationAmount?: number;
  effectiveDate?: Date;
  error?: string;
}

export interface WebhookEvent {
  provider: PaymentProviderId;
  eventType: string;
  eventId: string;
  data: WebhookData;
  signature?: string;
  timestamp: number;
}

export class PaymentService {
  /**
   * Get available payment providers
   */
  getAvailableProviders(): PaymentProvider[] {
    const providers: PaymentProvider[] = [];

    // Stripe provider
    if (ENV_CONFIG.PAYMENT.STRIPE_PUBLIC_KEY) {
      providers.push({
        id: 'stripe',
        name: 'Credit Card',
        enabled: ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.MONETIZATION.MARKETPLACE_ENABLED,
        supportedMethods: ['subscription', 'one_time']
      });
    }

    // Apple IAP provider
    if (ENV_CONFIG.PAYMENT.APPLE_IAP_ENABLED) {
      providers.push({
        id: 'apple',
        name: 'Apple In-App Purchase',
        enabled: ENV_CONFIG.MONETIZATION.IN_APP_PURCHASES_ENABLED,
        supportedMethods: ['subscription', 'one_time']
      });
    }

    // Google IAP provider
    if (ENV_CONFIG.PAYMENT.GOOGLE_IAP_ENABLED) {
      providers.push({
        id: 'google',
        name: 'Google Play Billing',
        enabled: ENV_CONFIG.MONETIZATION.IN_APP_PURCHASES_ENABLED,
        supportedMethods: ['subscription', 'one_time']
      });
    }

    return providers;
  }

  /**
   * Get preferred payment provider for platform
   */
  getPreferredProvider(): PaymentProviderId {
    // Determine platform and return appropriate provider
    if (typeof window !== 'undefined') {
      // Web platform - prefer Stripe
      return 'stripe';
    }

    // Mobile platform - prefer platform-specific IAP
    if (Platform.OS === 'ios') {
      return 'apple';
    } else if (Platform.OS === 'android') {
      return 'google';
    }

    return 'stripe'; // Default fallback
  }

  /**
   * Create a subscription
   */
  async createSubscription(request: CreateSubscriptionRequest, paymentData?: PaymentMetadata): Promise<PaymentResult> {
    try {
      // If monetization is disabled, simulate success
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
        console.log('Subscriptions disabled, simulating success');
        return { success: true, subscriptionId: 'simulated-' + Date.now() };
      }

      switch (request.provider) {
        case 'stripe':
          return await this.createStripeSubscription(request, paymentData);
        case 'apple':
          return await this.createAppleSubscription(request);
        case 'google':
          return await this.createGoogleSubscription(request);
        default:
          throw new Error(`Unsupported payment provider: ${request.provider}`);
      }
    } catch (error) {
      console.error('Failed to create subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Process one-time payment
   */
  async processOneTimePayment(request: CreatePaymentRequest): Promise<PaymentResult> {
    try {
      // If marketplace is disabled, simulate success
      if (!ENV_CONFIG.MONETIZATION.MARKETPLACE_ENABLED) {
        console.log('Marketplace disabled, simulating payment success');
        return { success: true, paymentId: 'simulated-' + Date.now() };
      }

      switch (request.provider) {
        case 'stripe':
          return await this.processStripePayment(request);
        case 'apple':
          return await this.processApplePayment(request);
        case 'google':
          return await this.processGooglePayment(request);
        default:
          throw new Error(`Unsupported payment provider: ${request.provider}`);
      }
    } catch (error) {
      console.error('Failed to process payment:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Handle webhook events from payment providers
   */
  async handleWebhook(event: WebhookEvent): Promise<void> {
    try {
      console.log(`Processing webhook from ${event.provider}: ${event.eventType}`);

      switch (event.provider) {
        case 'stripe':
          await this.handleStripeWebhook(event);
          break;
        case 'apple':
          await this.handleAppleWebhook(event);
          break;
        case 'google':
          await this.handleGoogleWebhook(event);
          break;
        default:
          console.warn(`Unknown webhook provider: ${event.provider}`);
      }
    } catch (error) {
      console.error('Failed to handle webhook:', error);
      throw error;
    }
  }

  /**
   * Sync subscription status across platforms
   */
  async syncSubscriptionStatus(userId: string): Promise<void> {
    try {
      // Get user's subscription from database
      const subscription = await subscriptionService.getUserSubscription(userId);
      if (!subscription) {
        return;
      }

      // Sync with payment provider
      switch (subscription.paymentProvider) {
        case 'stripe':
          await this.syncStripeSubscription(subscription.paymentProviderId);
          break;
        case 'apple':
          await this.syncAppleSubscription(subscription.paymentProviderId);
          break;
        case 'google':
          await this.syncGoogleSubscription(subscription.paymentProviderId);
          break;
      }
    } catch (error) {
      console.error('Failed to sync subscription status:', error);
      throw error;
    }
  }

  /**
   * Change subscription plan with proration
   */
  async changeSubscriptionPlan(request: ChangeSubscriptionRequest): Promise<SubscriptionChangeResult> {
    try {
      console.log('🔄 [PaymentService] Starting plan change:', request);

      // Get user's current subscription
      const subscription = await subscriptionService.getUserSubscription(request.userId);
      if (!subscription || subscription.status !== 'active') {
        throw new Error('No active subscription found');
      }

      // Get new plan details
      const { getSubscriptionPlan } = await import('../config/subscriptionPlans');
      const newPlan = getSubscriptionPlan(normalizePlanId(request.newPlanId));
      if (!newPlan) {
        throw new Error(`Plan ${request.newPlanId} not found`);
      }

      const currentBillingInterval = subscription.billingInterval || 'monthly';
      const targetBillingInterval = request.newBillingInterval || currentBillingInterval;

      console.log('🔄 [PaymentService] Plan change details:', {
        currentPlan: subscription.subscriptionType,
        targetPlan: newPlan.tier,
        currentBilling: currentBillingInterval,
        targetBilling: targetBillingInterval,
        isPlanChange: subscription.subscriptionType !== newPlan.tier,
        isBillingChange: currentBillingInterval !== targetBillingInterval
      });

      // If monetization is disabled, simulate the change
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED) {
        console.log('🔄 [PaymentService] Monetization disabled, simulating plan change');
        await subscriptionService.changeSubscriptionPlan(
          request.userId,
          request.newPlanId,
          request.prorationBehavior || 'immediate',
          targetBillingInterval
        );
        return {
          success: true,
          subscriptionId: subscription.id,
          effectiveDate: new Date(),
          prorationAmount: 0
        };
      }

      // Handle plan change based on payment provider
      let result: SubscriptionChangeResult;
      switch (subscription.paymentProvider) {
        case 'stripe':
          result = await this.changeStripeSubscriptionPlan(
            subscription,
            newPlan,
            targetBillingInterval,
            request.prorationBehavior
          );
          break;
        case 'apple':
          result = await this.changeAppleSubscriptionPlan(subscription, newPlan);
          break;
        case 'google':
          result = await this.changeGoogleSubscriptionPlan(subscription, newPlan);
          break;
        default:
          throw new Error(`Unsupported payment provider: ${subscription.paymentProvider}`);
      }

      // If successful, the Edge Function has already updated the database
      // No need to call subscriptionService.changeSubscriptionPlan() as it would overwrite
      // the correct updates made by the Edge Function
      if (result.success) {
        console.log('✅ [PaymentService] Plan change completed successfully - database updated by Edge Function');
      }

      return result;
    } catch (error) {
      console.error('❌ [PaymentService] Plan change failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Cancel subscription across all platforms
   */
  async cancelSubscription(userId: string, cancelAtPeriodEnd: boolean = true): Promise<boolean> {
    try {
      // Get user's subscription
      const subscription = await subscriptionService.getUserSubscription(userId);
      if (!subscription) {
        return false;
      }

      // Cancel with payment provider
      switch (subscription.paymentProvider) {
        case 'stripe':
          await stripeService.cancelSubscription(subscription.paymentProviderId, cancelAtPeriodEnd);
          break;
        case 'apple':
          await this.cancelAppleSubscription(subscription.paymentProviderId);
          break;
        case 'google':
          await this.cancelGoogleSubscription(subscription.paymentProviderId);
          break;
      }

      // Update local subscription
      return await subscriptionService.cancelSubscription(userId, cancelAtPeriodEnd);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      return false;
    }
  }

  /**
   * Validate payment provider configuration
   */
  validateConfiguration(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check Stripe configuration
    if (ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.MONETIZATION.MARKETPLACE_ENABLED) {
      if (!ENV_CONFIG.PAYMENT.STRIPE_PUBLIC_KEY) {
        errors.push('Stripe public key is required');
      }
    }

    // Check IAP configuration
    if (ENV_CONFIG.MONETIZATION.IN_APP_PURCHASES_ENABLED) {
      if (!ENV_CONFIG.PAYMENT.APPLE_IAP_ENABLED && !ENV_CONFIG.PAYMENT.GOOGLE_IAP_ENABLED) {
        errors.push('At least one IAP provider must be enabled');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Private methods for provider-specific implementations

  private async createStripeSubscription(request: CreateSubscriptionRequest, paymentData?: PaymentMetadata): Promise<PaymentResult> {
    try {
      // Get user profile
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('stripe_customer_id, email, name')
        .eq('id', request.userId)
        .single();

      if (!userProfile) {
        throw new Error('User profile not found');
      }

      // Use enhanced Stripe service to create/get customer
      let customerId = userProfile.stripe_customer_id;

      // Validate that customer exists in Stripe (important when switching between test/live modes)
      let customerExists = false;
      if (customerId) {
        try {
          const customer = await enhancedStripeService.getCustomer(customerId);
          customerExists = !!customer;
          console.log(`✅ Existing customer validated: ${customerId}`);
        } catch (error) {
          console.log(`⚠️  Customer ${customerId} not found in Stripe, will create new one`);
          customerExists = false;
        }
      }

      if (!customerId || !customerExists) {
        console.log('🔧 Creating new Stripe customer...');
        customerId = await enhancedStripeService.createCustomer(
          request.userId,
          userProfile.email,
          userProfile.name
        );

        // Update user profile with new Stripe customer ID
        await supabase
          .from('user_profiles')
          .update({ stripe_customer_id: customerId })
          .eq('id', request.userId);

        console.log(`✅ New customer created and stored: ${customerId}`);
      }

      // Get subscription plan
      const plans = await subscriptionService.getAvailablePlans();
      const plan = plans.find(p => p.id === normalizePlanId(request.planId));
      if (!plan) {
        throw new Error('Subscription plan not found');
      }

      // Get Stripe price ID from environment config
      const priceId = this.getStripePriceId(request.planId, request.billingInterval);
      if (!priceId) {
        throw new Error(`Stripe price ID not configured for plan ${request.planId} (${request.billingInterval})`);
      }

      console.log(`🎯 Creating Stripe subscription for customer ${customerId} with price ${priceId}`);
      console.log(`💳 Payment data provided: ${!!paymentData}`);
      console.log(`🔑 Customer verified in Stripe: ${customerId}`);

      const paymentMethodId = typeof paymentData?.paymentMethodId === 'string'
        ? paymentData.paymentMethodId
        : undefined;

      if (!paymentMethodId) {
        const origin = typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://kingdombiblequiz.com';
        const session = await enhancedStripeService.createCheckoutSession(
          customerId,
          priceId,
          `${origin}/Me?checkout=success&plan=${encodeURIComponent(normalizePlanId(request.planId))}`,
          `${origin}/Me?checkout=cancelled&plan=${encodeURIComponent(normalizePlanId(request.planId))}`,
          {
            user_id: request.userId,
            plan_id: normalizePlanId(request.planId),
            billing_interval: request.billingInterval,
          }
        );

        return {
          success: true,
          paymentId: session.id,
          checkoutUrl: session.url,
          requiresAction: true,
          nextAction: {
            type: 'redirect_to_url',
            url: session.url,
          },
        };
      }

      // Create Stripe subscription using enhanced service
      // Pass card details if provided
      const stripeSubscription = await enhancedStripeService.createSubscription(
        customerId,
        priceId,
        paymentMethodId
      );

      // Sync subscription to database (this creates the proper database record with Stripe linking)
      await enhancedStripeService.syncSubscriptionToDatabase(stripeSubscription.id);

      // Get the synced subscription record from database instead of creating a duplicate
      const { data: syncedSubscription, error: fetchError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', request.userId)
        .eq('provider_subscription_id', stripeSubscription.id)
        .single();

      if (fetchError || !syncedSubscription) {
        throw new Error('Failed to retrieve synced subscription from database');
      }

      console.log(`✅ Successfully created and synced Stripe subscription: ${stripeSubscription.id}`);
      console.log(`✅ Database record ID: ${syncedSubscription.id}`);

      return {
        success: true,
        subscriptionId: syncedSubscription.id,
        paymentId: stripeSubscription.id,
        clientSecret: stripeSubscription.latest_invoice?.payment_intent?.client_secret,
        requiresAction: stripeSubscription.status === 'incomplete'
      };
    } catch (error) {
      console.error('Failed to create Stripe subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  private async createAppleSubscription(request: CreateSubscriptionRequest): Promise<PaymentResult> {
    try {
      // Map plan ID to SKU
      const sku = this.mapPlanIdToSku(request.planId, request.billingInterval);
      
      // Use React Native payment service for iOS
      const result = await reactNativePaymentService.purchaseSubscription(sku);
      
      if (result.success) {
        // Create local subscription record
        const subscription = await subscriptionService.createSubscription({
          userId: request.userId,
          planId: request.planId,
          billingInterval: request.billingInterval,
          paymentProvider: 'apple',
          paymentProviderId: result.transactionId || '',
          customerEmail: request.customerEmail
        });

        return {
          success: true,
          subscriptionId: subscription.id,
          paymentId: result.transactionId
        };
      }

      return result;
    } catch (error) {
      console.error('Failed to create Apple subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Apple IAP error' };
    }
  }

  private async createGoogleSubscription(request: CreateSubscriptionRequest): Promise<PaymentResult> {
    try {
      // Map plan ID to SKU
      const sku = this.mapPlanIdToSku(request.planId, request.billingInterval);
      
      // Use React Native payment service for Android
      const result = await reactNativePaymentService.purchaseSubscription(sku);
      
      if (result.success) {
        // Create local subscription record
        const subscription = await subscriptionService.createSubscription({
          userId: request.userId,
          planId: request.planId,
          billingInterval: request.billingInterval,
          paymentProvider: 'google',
          paymentProviderId: result.transactionId || '',
          customerEmail: request.customerEmail
        });

        return {
          success: true,
          subscriptionId: subscription.id,
          paymentId: result.transactionId
        };
      }

      return result;
    } catch (error) {
      console.error('Failed to create Google subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Google Play Billing error' };
    }
  }

  private async processStripePayment(request: CreatePaymentRequest): Promise<PaymentResult> {
    try {
      // Get or create Stripe customer
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('stripe_customer_id, email, name')
        .eq('id', request.userId)
        .single();

      if (!userProfile) {
        throw new Error('User profile not found');
      }

      let customerId = userProfile.stripe_customer_id;
      if (!customerId) {
        customerId = await stripeService.createCustomer(
          request.userId,
          userProfile.email,
          userProfile.name
        );
      }

      // Create payment intent
      const paymentIntent = await stripeService.createPaymentIntent(
        customerId,
        request.amount,
        request.metadata
      );

      return {
        success: true,
        paymentId: paymentIntent.id,
        clientSecret: paymentIntent.client_secret,
        requiresAction: paymentIntent.status === 'requires_action'
      };
    } catch (error) {
      console.error('Failed to process Stripe payment:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  private async processApplePayment(request: CreatePaymentRequest): Promise<PaymentResult> {
    try {
      // Map item ID to SKU for event passes
      const sku = this.mapItemIdToEventSku(request.itemId);
      
      // Use React Native payment service for iOS event passes
      const result = await reactNativePaymentService.purchaseEventPass(sku);
      
      return result;
    } catch (error) {
      console.error('Failed to process Apple payment:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Apple IAP payment error' };
    }
  }

  private async processGooglePayment(request: CreatePaymentRequest): Promise<PaymentResult> {
    try {
      // Map item ID to SKU for event passes
      const sku = this.mapItemIdToEventSku(request.itemId);
      
      // Use React Native payment service for Android event passes
      const result = await reactNativePaymentService.purchaseEventPass(sku);
      
      return result;
    } catch (error) {
      console.error('Failed to process Google payment:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Google Play Billing payment error' };
    }
  }

  private async handleStripeWebhook(event: WebhookEvent): Promise<void> {
    // Use the enhanced Stripe service webhook handler
    const webhookData = {
      body: JSON.stringify(event.data),
      signature: event.signature || '',
      timestamp: event.timestamp
    };

    // Import webhook handler
    const { stripeWebhookHandler } = await import('../webhooks/stripeWebhookHandler');
    await stripeWebhookHandler.processWebhook(webhookData);
  }

  private async handleAppleWebhook(_event: WebhookEvent): Promise<void> {
    // Apple server-to-server notifications handling would go here
    console.log('Apple webhook handling not yet implemented');
  }

  private async handleGoogleWebhook(_event: WebhookEvent): Promise<void> {
    // Google Real-time Developer Notifications handling would go here
    console.log('Google webhook handling not yet implemented');
  }

  private async syncStripeSubscription(subscriptionId: string): Promise<void> {
    const subscription = await stripeService.getSubscription(subscriptionId);
    if (subscription) {
      // Update local subscription status
      const status = isStripeSubscriptionStatus(subscription.status)
        ? subscription.status
        : 'canceled';
      
      await subscriptionService.updateSubscriptionStatus(
        subscriptionId,
        status,
        { synced_at: new Date().toISOString() }
      );
    }
  }

  private async syncAppleSubscription(_subscriptionId: string): Promise<void> {
    // Apple subscription sync would go here
    console.log('Apple subscription sync not yet implemented');
  }

  private async syncGoogleSubscription(_subscriptionId: string): Promise<void> {
    // Google subscription sync would go here
    console.log('Google subscription sync not yet implemented');
  }

  private async cancelAppleSubscription(_subscriptionId: string): Promise<void> {
    // Apple subscription cancellation would go here
    console.log('Apple subscription cancellation not yet implemented');
  }

  private async cancelGoogleSubscription(_subscriptionId: string): Promise<void> {
    // Google subscription cancellation would go here
    console.log('Google subscription cancellation not yet implemented');
  }


  /**
   * Map internal plan ID to mobile store SKU
   */
  private mapPlanIdToSku(planId: string, billingInterval: BillingInterval): StoreSubscriptionSku {
    const normalizedPlanId = normalizePlanId(planId);
    const planSkus = MOBILE_PLAN_SKU_MAP[normalizedPlanId];
    const sku = planSkus?.[billingInterval];
    if (!sku) {
      throw new Error(`No mobile store SKU configured for plan ${planId} (${billingInterval})`);
    }

    return sku;
  }

  /**
   * Map internal item ID to mobile store event pass SKU
   */
  private mapItemIdToEventSku(itemId: string): StoreEventPassSku {
    const sku = EVENT_PASS_SKU_MAP[itemId];
    if (!sku) {
      throw new Error(`Unknown event item ID: ${itemId}`);
    }

    return sku;
  }

  /**
   * Change Stripe subscription plan with proration and billing interval support
   */
  private async changeStripeSubscriptionPlan(
    subscription: UserSubscription,
    newPlan: SubscriptionPlan,
    newBillingInterval: BillingInterval,
    prorationBehavior?: 'immediate' | 'next_period'
  ): Promise<SubscriptionChangeResult> {
    try {
      // Get the Stripe price ID for the new plan and billing interval
      const priceId = this.getStripePriceId(newPlan.tier, newBillingInterval);

      console.log('🔄 [Stripe] Changing subscription:', {
        subscriptionId: subscription.paymentProviderId,
        currentPlan: subscription.subscriptionType,
        targetPlan: newPlan.tier,
        currentBilling: subscription.billingInterval || 'monthly',
        targetBilling: newBillingInterval,
        newPriceId: priceId
      });

      // Use Stripe service to modify subscription
      const result = await stripeService.modifySubscription(
        subscription.paymentProviderId,
        priceId,
        prorationBehavior === 'next_period' ? false : true, // immediate proration by default
        newBillingInterval // Pass the new billing interval
      );

      return {
        success: true,
        subscriptionId: result.id,
        effectiveDate: new Date(),
        prorationAmount: result.latest_invoice?.amount_due || 0
      };
    } catch (error) {
      console.error('Failed to change Stripe subscription:', error);
      throw error;
    }
  }

  /**
   * Change Apple subscription plan (requires user action in App Store)
   */
  private async changeAppleSubscriptionPlan(
    subscription: UserSubscription,
    _newPlan: SubscriptionPlan
  ): Promise<SubscriptionChangeResult> {
    try {
      // Apple plan changes require user action through App Store
      // We can only update our local record and guide the user
      console.log('Apple subscription plan change initiated - user action required');

      return {
        success: true,
        subscriptionId: subscription.id,
        effectiveDate: new Date(),
        // Apple handles proration automatically
      };
    } catch (error) {
      console.error('Failed to initiate Apple subscription change:', error);
      throw error;
    }
  }

  /**
   * Change Google subscription plan (requires user action in Play Store)
   */
  private async changeGoogleSubscriptionPlan(
    subscription: UserSubscription,
    _newPlan: SubscriptionPlan
  ): Promise<SubscriptionChangeResult> {
    try {
      // Google plan changes require user action through Play Store
      // We can only update our local record and guide the user
      console.log('Google subscription plan change initiated - user action required');

      return {
        success: true,
        subscriptionId: subscription.id,
        effectiveDate: new Date(),
        // Google handles proration automatically
      };
    } catch (error) {
      console.error('Failed to initiate Google subscription change:', error);
      throw error;
    }
  }

  /**
   * Get Stripe price mapping for all tiers and intervals
   */
  private getStripePriceMapping(): Record<CanonicalPaidPlanId, { monthly: string; annual: string }> {
    return {
      premium: {
        monthly: ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.STRIPE_MONTHLY_PRICE_ID,
        annual: ENV_CONFIG.SUBSCRIPTION_TIERS.FAMILY.STRIPE_ANNUAL_PRICE_ID,
      },
    };
  }

  /**
   * Get Stripe price ID for a plan tier and billing interval
   */
  private getStripePriceId(tier: string, billingInterval: BillingInterval): string {
    const priceMap = this.getStripePriceMapping();
    const planPrices = priceMap[normalizePlanId(tier)];

    if (!planPrices) {
      throw new Error(`No Stripe prices found for tier: ${tier}`);
    }

    const priceId = planPrices[billingInterval];
    if (!priceId) {
      throw new Error(`No Stripe price ID found for ${tier} ${billingInterval}`);
    }

    return priceId;
  }
}

// Singleton instance
export const paymentService = new PaymentService();

// Default export
export default paymentService;
